import { useMemo } from "react";
import { ElevationPoint } from "../../../mocks/elevationData";

interface Props {
  data: ElevationPoint[];
  destinationName: string;
}

const ALTITUDE_ZONES = [
  { min: 0, max: 2000, label: "平原 / 低地", color: "#dcfce7", textColor: "#15803d" },
  { min: 2000, max: 3500, label: "高原地帶", color: "#fef9c3", textColor: "#a16207" },
  { min: 3500, max: 5000, label: "高海拔地區", color: "#ffedd5", textColor: "#c2410c" },
  { min: 5000, max: 9000, label: "極高海拔", color: "#fee2e2", textColor: "#b91c1c" },
];

const getSmoothedPath = (points: [number, number][]): string => {
  if (points.length < 2) return "";
  let path = `M ${points[0][0].toFixed(2)} ${points[0][1].toFixed(2)}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(points.length - 1, i + 2)];
    const tension = 0.4;
    const cp1x = p1[0] + (p2[0] - p0[0]) * tension;
    const cp1y = p1[1] + (p2[1] - p0[1]) * tension;
    const cp2x = p2[0] - (p3[0] - p1[0]) * tension;
    const cp2y = p2[1] - (p3[1] - p1[1]) * tension;
    path += ` C ${cp1x.toFixed(2)} ${cp1y.toFixed(2)} ${cp2x.toFixed(2)} ${cp2y.toFixed(2)} ${p2[0].toFixed(2)} ${p2[1].toFixed(2)}`;
  }
  return path;
};

const ElevationChart = ({ data, destinationName }: Props) => {
  const vbW = 900;
  const vbH = 260;
  const padL = 58;
  const padR = 24;
  const padT = 36;
  const padB = 42;
  const chartW = vbW - padL - padR;
  const chartH = vbH - padT - padB;

  const maxElev = useMemo(() => {
    const raw = Math.max(...data.map((d) => d.maxElevation));
    if (raw <= 2000) return 2500;
    if (raw <= 3500) return 4000;
    if (raw <= 5000) return 5500;
    return Math.ceil(raw / 1000) * 1000 + 500;
  }, [data]);

  const xPos = (i: number) =>
    data.length === 1 ? padL + chartW / 2 : padL + (i / (data.length - 1)) * chartW;
  const yPos = (e: number) => padT + (1 - e / maxElev) * chartH;

  const svgPoints = useMemo<[number, number][]>(
    () => data.map((d, i) => [xPos(i), yPos(d.maxElevation)]),
    [data, maxElev]
  );

  const linePath = useMemo(() => getSmoothedPath(svgPoints), [svgPoints]);

  const areaPath = useMemo(() => {
    if (svgPoints.length === 0) return "";
    const bottom = padT + chartH;
    return (
      linePath +
      ` L ${svgPoints[svgPoints.length - 1][0].toFixed(2)} ${bottom}` +
      ` L ${svgPoints[0][0].toFixed(2)} ${bottom} Z`
    );
  }, [linePath, svgPoints, padT, chartH]);

  // Reference elevations to show as dashed lines
  const refLines = useMemo(() => {
    const all = [1000, 2000, 3000, 3500, 4000, 4500, 5000].filter(
      (v) => v < maxElev
    );
    return all;
  }, [maxElev]);

  // Active altitude zones within chart range
  const activeZones = useMemo(() =>
    ALTITUDE_ZONES.filter((z) => z.min < maxElev),
    [maxElev]
  );

  // Badge for danger level
  const peakElev = Math.max(...data.map((d) => d.maxElevation));
  const dangerLevel =
    peakElev >= 5000
      ? { text: "極高海拔路線", color: "bg-red-100 text-red-700 border-red-200" }
      : peakElev >= 3500
      ? { text: "高海拔路線", color: "bg-orange-100 text-orange-700 border-orange-200" }
      : peakElev >= 2000
      ? { text: "高原路線", color: "bg-yellow-100 text-yellow-700 border-yellow-200" }
      : null;

  return (
    <div className="mb-8 bg-white rounded-2xl border border-stone-200 overflow-hidden">
      {/* Header */}
      <div className="px-5 pt-5 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-full">
            <i className="ri-line-chart-line text-amber-600 text-base"></i>
          </div>
          <div>
            <p className="text-xs text-gray-400">全程海拔變化</p>
            <p className="text-sm font-semibold text-gray-800">{destinationName} · 每日最高海拔</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {dangerLevel && (
            <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${dangerLevel.color}`}>
              {dangerLevel.text}
            </span>
          )}
          <span className="text-xs text-gray-400 bg-stone-50 px-2.5 py-1 rounded-full border border-stone-100">
            峰值 {peakElev.toLocaleString()} m
          </span>
        </div>
      </div>

      {/* SVG Chart */}
      <div className="px-2 pb-1">
        <svg
          viewBox={`0 0 ${vbW} ${vbH}`}
          className="w-full"
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <linearGradient id="elevGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#f97316" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#fbbf24" stopOpacity="0.05" />
            </linearGradient>
            <clipPath id="chartClip">
              <rect x={padL} y={padT} width={chartW} height={chartH} />
            </clipPath>
          </defs>

          {/* Altitude zone bands */}
          {activeZones.map((zone) => {
            const yTop = yPos(Math.min(zone.max, maxElev));
            const yBottom = yPos(zone.min);
            if (yBottom <= padT) return null;
            return (
              <rect
                key={zone.min}
                x={padL}
                y={yTop}
                width={chartW}
                height={Math.max(0, yBottom - yTop)}
                fill={zone.color}
                opacity={0.6}
                clipPath="url(#chartClip)"
              />
            );
          })}

          {/* Reference lines */}
          {refLines.map((elev) => (
            <g key={elev}>
              <line
                x1={padL}
                y1={yPos(elev)}
                x2={padL + chartW}
                y2={yPos(elev)}
                stroke="#d1d5db"
                strokeWidth="0.8"
                strokeDasharray="4 3"
              />
              <text
                x={padL - 4}
                y={yPos(elev) + 4}
                textAnchor="end"
                fontSize="11"
                fill="#9ca3af"
              >
                {elev >= 1000 ? `${(elev / 1000).toFixed(elev % 1000 === 0 ? 0 : 1)}k` : elev}
              </text>
            </g>
          ))}

          {/* Y-axis label */}
          <text
            transform={`rotate(-90, 14, ${padT + chartH / 2})`}
            x={14}
            y={padT + chartH / 2}
            textAnchor="middle"
            fontSize="11"
            fill="#9ca3af"
          >
            海拔 (m)
          </text>

          {/* Area fill */}
          <path d={areaPath} fill="url(#elevGrad)" clipPath="url(#chartClip)" />

          {/* Line stroke */}
          <path
            d={linePath}
            fill="none"
            stroke="#f97316"
            strokeWidth="2.5"
            strokeLinejoin="round"
            clipPath="url(#chartClip)"
          />

          {/* Data points + labels */}
          {data.map((point, i) => {
            const cx = xPos(i);
            const cy = yPos(point.maxElevation);
            const isHighAlert = point.maxElevation >= 4000;
            const isPeak = point.maxElevation === peakElev;
            const elevText =
              point.maxElevation >= 1000
                ? `${(point.maxElevation / 1000).toFixed(point.maxElevation % 100 === 0 ? 1 : 2)}k`
                : `${point.maxElevation}`;

            // Decide label position: above or below depending on space
            const labelY = cy - 20;
            const showAbove = labelY > padT + 14;

            return (
              <g key={point.day}>
                {/* Vertical guide line to x-axis */}
                <line
                  x1={cx}
                  y1={cy}
                  x2={cx}
                  y2={padT + chartH}
                  stroke="#e5e7eb"
                  strokeWidth="0.8"
                  strokeDasharray="3 3"
                />

                {/* Outer ring for peak */}
                {isPeak && (
                  <circle cx={cx} cy={cy} r="10" fill="#fef3c7" stroke="#f59e0b" strokeWidth="1.5" />
                )}

                {/* Dot */}
                <circle
                  cx={cx}
                  cy={cy}
                  r={isPeak ? 5 : 4}
                  fill={isHighAlert ? "#ef4444" : "#f97316"}
                  stroke="white"
                  strokeWidth="2"
                />

                {/* Elevation value */}
                <text
                  x={cx}
                  y={showAbove ? labelY : cy + 22}
                  textAnchor="middle"
                  fontSize="11.5"
                  fontWeight={isPeak ? "700" : "600"}
                  fill={isHighAlert ? "#dc2626" : "#ea580c"}
                >
                  {elevText}m
                </text>

                {/* Landmark label (only show for notable points) */}
                {point.label && (isPeak || isHighAlert || i === 0 || i === data.length - 1) && (
                  <text
                    x={cx}
                    y={showAbove ? labelY - 13 : cy + 35}
                    textAnchor="middle"
                    fontSize="10"
                    fill="#6b7280"
                  >
                    {point.label}
                  </text>
                )}

                {/* Day label on x-axis */}
                <text
                  x={cx}
                  y={padT + chartH + 18}
                  textAnchor="middle"
                  fontSize="11"
                  fill="#9ca3af"
                >
                  D{point.day}
                </text>
              </g>
            );
          })}

          {/* Border frame */}
          <rect
            x={padL}
            y={padT}
            width={chartW}
            height={chartH}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth="1"
          />
        </svg>
      </div>

      {/* Zone legend */}
      <div className="px-5 pb-4 flex flex-wrap gap-3">
        {ALTITUDE_ZONES.map((z) => (
          <span key={z.min} className="flex items-center gap-1.5 text-xs" style={{ color: z.textColor }}>
            <span
              className="inline-block w-3 h-3 rounded-sm border"
              style={{ background: z.color, borderColor: z.textColor + "40" }}
            ></span>
            {z.label} ({z.min >= 1000 ? `${z.min / 1000}k` : z.min}–
            {z.max >= 1000 ? `${z.max / 1000}k` : z.max}m)
          </span>
        ))}
      </div>
    </div>
  );
};

export default ElevationChart;
